import { FormControl, FormLabel, Select } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OfficeJSON } from '../../entities/Office';
import { sortAlphabeticallyBy } from '../../utils/arrays';
import AddressSearch, { CitySearchParams } from '../AddressSearch';
import { Box } from '../primitives/Box';
import { Checkbox } from '../primitives/Checkbox';
import { HStack } from '../primitives/stack/HStack';
import { Text } from '../primitives/Text';

interface Props {
  defaultValue?: string;
  officeId?: number | null;
  label?: string;
  offices: OfficeJSON[];
  onCityChange(city: CitySearchParams | null): void;
  onOfficeChange?(officeId: number | null): void;
}

export function LocationSelector(props: Props) {
  const { t } = useTranslation('common', { keyPrefix: 'fields' });
  const hasOffices = props.offices.length > 0;

  const [isInOffice, setIsInOffice] = useState(typeof props.officeId === 'number');

  const officeOptions = useMemo(
    () => sortAlphabeticallyBy(props.offices, 'name').map(({ id, name }) => (
      <option key={id} value={id}>{name}</option>
    )),
    [props.offices]
  );

  function onInOfficeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const checked = e.target.checked;

    setIsInOffice(checked);

    if (checked) {
      props.onCityChange(null);
    } else {
      props.onOfficeChange?.(null);
    }
  }

  function onOfficeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const officeId = e.target.value ? Number(e.target.value) : null;
    props.onOfficeChange?.(officeId);
  }

  return (
    <Box w="100%" position="relative">
      {
        isInOffice ? (
          <FormControl id="officeId" isRequired>
            <FormLabel>{props.label || t('location.label')}</FormLabel>
            <Select
              name="officeId"
              size="sm"
              bg="white"
              value={props.officeId ?? ''}
              onChange={onOfficeChange}
            >
              <option value="">{t('office.placeholder')}</option>
              { officeOptions }
              <option value="0">{t('office.another')}</option>
            </Select>
          </FormControl>
        ) : (
          <FormControl id="location" isRequired>
            <FormLabel>{props.label || t('location.label')}</FormLabel>

            <AddressSearch
              defaultValue={props.defaultValue}
              types={['(cities)']}
              onChange={props.onCityChange}
            />
          </FormControl>
        )
      }

      {
        hasOffices && (
          <HStack spacing={2} top={0} right={0} position="absolute">
            <Text fontWeight={500}>{t('inOurOffice.label')}</Text>

            <Checkbox
              id="isInOffice"
              isChecked={isInOffice}
              onChange={onInOfficeChange}
            />
          </HStack>
        )
      }
    </Box>
  );
}
